import React, { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { SUpdateKoboColumnMappings } from "../../../../apis/app.service";
import { IKoboProject } from "../../core/models";
import toast from "react-hot-toast";
import "./ConfigureColumnMappings.scss";

interface ConfigureColumnMappingsProps {
  project: IKoboProject;
  availableColumns: string[];
  onClose: () => void;
  onUpdate?: (updatedProject: IKoboProject) => void;
}

function ConfigureColumnMappings({
  project,
  availableColumns,
  onClose,
  onUpdate,
}: ConfigureColumnMappingsProps) {
  const queryClient = useQueryClient();

  const [satisfactionColumn, setSatisfactionColumn] = useState(
    project.columnMappings?.satisfactionColumn || "",
  );
  const [waitingTimeColumn, setWaitingTimeColumn] = useState(
    project.columnMappings?.waitingTimeColumn || "",
  );
  const [locationColumn, setLocationColumn] = useState(
    project.columnMappings?.locationColumn || "",
  );
  const [departmentColumn, setDepartmentColumn] = useState(
    project.columnMappings?.departmentColumn || "",
  );

  const updateMappingsMutation = useMutation({
    mutationFn: () =>
      SUpdateKoboColumnMappings(project._id, {
        satisfactionColumn: satisfactionColumn || undefined,
        waitingTimeColumn: waitingTimeColumn || undefined,
        locationColumn: locationColumn || undefined,
        departmentColumn: departmentColumn || undefined,
      }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["koboProjects"] });
      queryClient.invalidateQueries({
        queryKey: ["koboAssetDetails", project._id],
      });
      // Column mappings affect how analytics data is aggregated — clear cached analytics
      // so the next load triggers a fresh sync with the updated mappings
      queryClient.invalidateQueries({ queryKey: ["koboRatingTrends"] });
      queryClient.invalidateQueries({ queryKey: ["koboWaitingTimeTrends"] });
      queryClient.invalidateQueries({ queryKey: ["koboOrganizationStats"] });
      queryClient.invalidateQueries({ queryKey: ["koboOrganizations"] });

      // Call onUpdate callback with fresh data
      if (onUpdate && data.data) {
        onUpdate(data.data);
      }

      toast.success("Column mappings updated successfully", {
        duration: 3000,
        position: "top-right",
      });
      onClose();
    },
    onError: (error: Error) => {
      toast.error(`Failed to update mappings: ${error.message}`, {
        duration: 3000,
        position: "top-right",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateMappingsMutation.mutate();
  };

  return (
    <div className="configure-mappings-modal" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Configure Column Mappings</h3>
          <button className="close-btn" onClick={onClose}>
            <i className="bx bx-x"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <p className="modal-description">
              Map the columns from your Kobo survey to the analytics fields.
              This ensures accurate data reporting across different survey
              formats.
            </p>

            <div className="form-group">
              <label htmlFor="satisfactionColumn">
                Satisfaction Rating Column
                <span className="field-description">
                  Column containing satisfaction ratings (1-5 scale)
                </span>
              </label>
              <select
                id="satisfactionColumn"
                value={satisfactionColumn}
                onChange={(e) => setSatisfactionColumn(e.target.value)}
                className="form-control"
              >
                <option value="">-- Select Column --</option>
                {availableColumns.map((col) => (
                  <option key={col} value={col}>
                    {col}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="waitingTimeColumn">
                Waiting Time Column
                <span className="field-description">
                  Column containing waiting time in minutes
                </span>
              </label>
              <select
                id="waitingTimeColumn"
                value={waitingTimeColumn}
                onChange={(e) => setWaitingTimeColumn(e.target.value)}
                className="form-control"
              >
                <option value="">-- Select Column --</option>
                {availableColumns.map((col) => (
                  <option key={col} value={col}>
                    {col}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="locationColumn">
                Location/Organization Column
                <span className="field-description">
                  Column containing office or organization names
                </span>
              </label>
              <select
                id="locationColumn"
                value={locationColumn}
                onChange={(e) => setLocationColumn(e.target.value)}
                className="form-control"
              >
                <option value="">-- Select Column --</option>
                {availableColumns.map((col) => (
                  <option key={col} value={col}>
                    {col}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="departmentColumn">
                Department Column
                <span className="field-description">
                  Column containing department names
                </span>
              </label>
              <select
                id="departmentColumn"
                value={departmentColumn}
                onChange={(e) => setDepartmentColumn(e.target.value)}
                className="form-control"
              >
                <option value="">-- Select Column --</option>
                {availableColumns.map((col) => (
                  <option key={col} value={col}>
                    {col}
                  </option>
                ))}
              </select>
            </div>

            <div className="info-box">
              <i className="bx bx-info-circle"></i>
              <p>
                These mappings are required for this project to appear correctly
                in the Analytics dashboard. You can update them anytime if your
                survey structure changes.
              </p>
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={updateMappingsMutation.isPending}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={updateMappingsMutation.isPending}
            >
              {updateMappingsMutation.isPending ? "Saving..." : "Save Mappings"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ConfigureColumnMappings;
